import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
    import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
    import * as THREE from 'three';

    document.querySelectorAll('[data-viewer]').forEach(wrapper => {
      const wrapperSize = wrapper.getBoundingClientRect();
      const targetElement = wrapper.querySelector('[data-viewer-target]');

      if (!targetElement) return;

      const debug = wrapper.querySelector('[data-viewer-debug]');
      const debugElements = {};
      if (debug) {
        debugElements.x = debug.querySelector('[data-x]');
        debugElements.y = debug.querySelector('[data-y]');
        debugElements.z = debug.querySelector('[data-z]');
        debugElements.zm = debug.querySelector('[data-zm]');

        Object.values(debugElements).forEach(el => el.addEventListener('click', async () => {
          const type = "text/plain";
          const clipboardItemData = {
            [type]: el.textContent,
          };
          const clipboardItem = new ClipboardItem(clipboardItemData);
          await navigator.clipboard.write([clipboardItem]);
        }));
      }

      const initialPosition = {
        x: wrapper.dataset.x,
        y: wrapper.dataset.y,
        z: wrapper.dataset.z,
        zm: wrapper.dataset.zm
      }

      const url = targetElement.getAttribute('data-src');

      const scene = new THREE.Scene();

      const camera = new THREE.PerspectiveCamera(45, wrapperSize.width / wrapperSize.height, 0.1, 1000);
      camera.position.set(0, 0, 5);

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(wrapperSize.width, wrapperSize.height);
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setClearColor(0x000000, 0);
      targetElement.appendChild(renderer.domElement);

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.enableZoom = false;
      controls.enablePan = false;

      const hasMouseover = wrapper.hasAttribute('data-mouseover');
      const touchOnly = wrapper.hasAttribute('data-mouseover-touch-only');
      const isFinePointer = window.matchMedia('(pointer: fine)').matches;
      // A hotspot in mode="mouseover" emits data-touch-only on its inner element,
      // and re-enables desktop mouseover (sourced from the outer) even if the figure
      // itself was flagged touch-only.
      const hotspotMouseoverMode = !!wrapper.querySelector('[data-mouseover-inner][data-touch-only]');
      const mouseoverMode = hasMouseover && isFinePointer && (!touchOnly || hotspotMouseoverMode);
      const touchHoverMode = hasMouseover && !isFinePointer;

      let initialSpherical = null;
      const mouseTarget = { x: 0, y: 0 };
      const currentOffset = { azimuth: 0, polar: 0 };
      const baseCameraPosition = new THREE.Vector3();
      let baseCaptured = false;

      if (mouseoverMode) {
        controls.enabled = false;

        const mouseoverSource = wrapper.querySelector('[data-mouseover-inner]:not([data-touch-only])')
          || wrapper.closest('[data-mouseover-outer]')
          || wrapper;

        mouseoverSource.addEventListener('mousemove', (e) => {
          const rect = mouseoverSource.getBoundingClientRect();
          mouseTarget.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
          mouseTarget.y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
        });

        mouseoverSource.addEventListener('mouseleave', () => {
          mouseTarget.x = 0;
          mouseTarget.y = 0;
        });
      }

      if (touchHoverMode) {
        const hoverSource = wrapper.querySelector('[data-mouseover-inner]')
          || wrapper.closest('[data-mouseover-outer]')
          || wrapper;

        if (touchOnly) {
          controls.enabled = false;
          renderer.domElement.style.touchAction = 'auto';
        }

        const minTouches = touchOnly ? 1 : 2;

        const updateFromTouches = (touches) => {
          const rect = hoverSource.getBoundingClientRect();
          let avgX = 0, avgY = 0;
          for (let i = 0; i < touches.length; i++) {
            avgX += touches[i].clientX;
            avgY += touches[i].clientY;
          }
          avgX /= touches.length;
          avgY /= touches.length;
          mouseTarget.x = ((avgX - rect.left) / rect.width) * 2 - 1;
          mouseTarget.y = ((avgY - rect.top) / rect.height) * 2 - 1;
        };

        const onTouch = (e) => {
          if (e.touches.length >= minTouches) {
            updateFromTouches(e.touches);
          } else {
            mouseTarget.x = 0;
            mouseTarget.y = 0;
          }
        };

        hoverSource.addEventListener('touchstart', onTouch, { passive: true });
        hoverSource.addEventListener('touchmove', onTouch, { passive: true });
        hoverSource.addEventListener('touchend', onTouch, { passive: true });
        hoverSource.addEventListener('touchcancel', onTouch, { passive: true });
      }

      if (debug) {
        controls.enableZoom = true;
        controls.addEventListener('change', () => {
          const { x, y, z } = camera.position;
          debugElements.x.textContent = x.toFixed(2);
          debugElements.y.textContent = y.toFixed(2);
          debugElements.z.textContent = z.toFixed(2);
          debugElements.zm.textContent = controls.getDistance().toFixed(2);
        });
      }

      const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
      directionalLight.position.set(5, 10, 7.5);
      scene.add(directionalLight);

      const loader = new GLTFLoader();
      loader.load(url, (gltf) => {
        const model = gltf.scene;
        scene.add(model);

        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        model.position.sub(center);

        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = camera.fov * (Math.PI / 180);
        const cameraDistance = parseFloat(initialPosition.zm) || (maxDim / (2 * Math.tan(fov / 2))) * 1.5;
        camera.position.set(
          parseFloat(initialPosition.x) || 0,
          parseFloat(initialPosition.y) || 0,
          parseFloat(initialPosition.z) || cameraDistance
        );

        controls.target.set(0, 0, 0);
        controls.update();

        if (mouseoverMode) {
          initialSpherical = new THREE.Spherical().setFromVector3(
            camera.position.clone().sub(controls.target)
          );
        }

        animate();

        wrapper.classList.remove('is-loading');

        resize();

      }, undefined, (error) => {
        console.error('Error loading model:', error);
      });

      function animate() {
        requestAnimationFrame(animate);

        const azimuthRange = Math.PI / 2;
        const polarRange = Math.PI / 6;

        if (mouseoverMode && initialSpherical) {
          currentOffset.azimuth += (mouseTarget.x * azimuthRange - currentOffset.azimuth) * 0.08;
          currentOffset.polar += (mouseTarget.y * polarRange - currentOffset.polar) * 0.08;

          const spherical = new THREE.Spherical(
            initialSpherical.radius,
            THREE.MathUtils.clamp(
              initialSpherical.phi + currentOffset.polar,
              0.1,
              Math.PI - 0.1
            ),
            initialSpherical.theta - currentOffset.azimuth
          );

          camera.position.setFromSpherical(spherical).add(controls.target);
          camera.lookAt(controls.target);
        } else if (touchHoverMode) {
          if (baseCaptured) camera.position.copy(baseCameraPosition);
          controls.update();
          baseCameraPosition.copy(camera.position);
          baseCaptured = true;

          currentOffset.azimuth += (mouseTarget.x * azimuthRange - currentOffset.azimuth) * 0.08;
          currentOffset.polar += (mouseTarget.y * polarRange - currentOffset.polar) * 0.08;

          const base = new THREE.Spherical().setFromVector3(
            baseCameraPosition.clone().sub(controls.target)
          );
          const spherical = new THREE.Spherical(
            base.radius,
            THREE.MathUtils.clamp(base.phi + currentOffset.polar, 0.1, Math.PI - 0.1),
            base.theta - currentOffset.azimuth
          );

          camera.position.setFromSpherical(spherical).add(controls.target);
          camera.lookAt(controls.target);
        } else {
          controls.update();
        }

        renderer.render(scene, camera);
      }

      const resize = () => {
        const newSize = wrapper.getBoundingClientRect();
        camera.aspect = newSize.width / newSize.height;
        camera.updateProjectionMatrix();
        renderer.setSize(newSize.width, newSize.height);
      };

      window.addEventListener('resize', () => resize())
    });
